"use client"

import { createContext, useContext } from "react"

import { AppSidebar } from "@/components/app-sidebar"
import { ImpersonationBanner } from "@/components/impersonation-banner"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { Eyebrow } from "@/components/ui/eyebrow"

/**
 * Optional band injected at the very top of the shell's content column, above
 * the page header. Defaults to `null`, so every page that does not opt in is
 * byte-for-byte unchanged.
 *
 * Exists because a route can own a cross-cutting band (the HR check-in strip on
 * the dashboard home) while the SHELL is rendered further down the tree by the
 * page's role variant — the route cannot wrap what it does not render, so it
 * hands the band down instead.
 */
const ApolloPageBandContext = createContext<React.ReactNode>(null)

interface ApolloPageBandProviderProps {
  /** Rendered bare — pass margins/`shrink-0` via the band's own className so a
   *  self-gating band that returns null leaves NO empty wrapper behind. */
  band: React.ReactNode
  children: React.ReactNode
}

/** Feeds `band` to whichever {@link ApolloPageShell} renders underneath. */
export function ApolloPageBandProvider({
  band,
  children,
}: ApolloPageBandProviderProps) {
  return (
    <ApolloPageBandContext.Provider value={band}>
      {children}
    </ApolloPageBandContext.Provider>
  )
}

interface ApolloPageShellProps {
  children: React.ReactNode
}

/**
 * Apollo fixed-viewport app shell.
 *
 * The shared SidebarProvider is a `min-h-svh` floor with body scroll; capping +
 * clipping it here (`!h-svh !overflow-hidden`) pins the header / stats / filter
 * bands and the pagination footer so only the inner content area scrolls. Uses
 * the same scaffolding as PageShell (Phase 76): stock rounded inset with
 * `md:border` and the push banner; there is no app-level SiteHeader row — the
 * mobile sidebar trigger lives in ApolloPageHeader.
 */
export function ApolloPageShell({ children }: ApolloPageShellProps) {
  const band = useContext(ApolloPageBandContext)

  return (
    <SidebarProvider
      className="!h-svh !overflow-hidden"
      style={
        { "--sidebar-width": "calc(var(--spacing) * 72)" } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="overflow-hidden md:border-l md:border-border">
        <ImpersonationBanner />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
          {band}
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

interface ApolloPageHeaderProps {
  /** Uppercase micro-label above the title (Eyebrow). */
  eyebrow: React.ReactNode
  /** Serif page title (h1). */
  title: React.ReactNode
  /** Optional muted subtitle line under the title. */
  subtitle?: React.ReactNode
  /** Optional metadata stats line under the title/subtitle (StatsMetaline). */
  stats?: React.ReactNode
  /** Optional right-aligned action cluster (buttons). */
  actions?: React.ReactNode
  /** Optional secondary row under the title/actions (e.g. a search input). */
  children?: React.ReactNode
}

/**
 * Apollo page header band — pinned eyebrow + serif h1 + muted subtitle on the
 * left, an optional action cluster on the right, and an optional secondary row
 * (search, segmented control) underneath. Mirrors the leads index header band
 * exactly; the clients/kartele variants collapse to it (no actions, and kartele
 * feeds its search input through `children`).
 *
 * On short viewports (`max-height: 900px` — a 1440x900 or 1280x800 laptop) the
 * band's vertical padding tightens and the serif h1 is capped, handing roughly
 * 30px back to the scroll area inside the fixed-height `ApolloPageShell`. The
 * eyebrow and subtitle stay visible; nothing changes above 900px, where the
 * band renders byte-identically to before.
 *
 * NOTE the `[@media(max-height:900px)]:` title cap must beat `xl:text-[40px]`.
 * It does: Tailwind v4 emits arbitrary variants after every named breakpoint
 * variant, so at equal specificity the later rule wins.
 */
export function ApolloPageHeader({
  eyebrow,
  title,
  subtitle,
  stats,
  actions,
  children,
}: ApolloPageHeaderProps) {
  return (
    <div className="shrink-0 border-b border-border px-4 py-5 md:px-6 [@media(max-height:900px)]:py-3">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          <SidebarTrigger className="-ml-1 mt-0.5 md:hidden" />
          <div className="min-w-0">
          <Eyebrow className="mb-1.5 block">{eyebrow}</Eyebrow>
          <h1 className="font-serif text-[30px] font-normal leading-[1.05] tracking-[-0.02em] md:text-[36px] xl:text-[40px] [@media(max-height:900px)]:text-[26px]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1.5 text-[13.5px] text-muted-foreground">
              {subtitle}
            </p>
          )}
          {stats && <div className="mt-2.5">{stats}</div>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  )
}
